import type { ProblemDetails } from "../../shared/types/problem-details.type";

export interface CreateTeamPayload {
  teamName: string;
  description: string;
}

export interface TeamMutationRequest {
  name: string;
  description: string;
}

export interface TeamMutationResponse {
  id: string;
  name: string;
  description: string;
}

export interface TeamMutationUiResponse {
  id: string;
  teamName: string;
  description: string;
}

export type CreateTeamResponse = TeamMutationUiResponse;

export interface UpdateTeamPayload {
  teamId: string;
  teamName: string;
  description: string;
}

export type UpdateTeamResponse = TeamMutationUiResponse;

export const TEAM_STATUS = {
  Active: "active",
  Deleting: "deleting",
  MovingData: "movingData",
} as const;

export type TeamStatus = (typeof TEAM_STATUS)[keyof typeof TEAM_STATUS];

export interface Team {
  id: string;
  name: string;
  description: string;
  isDefault: boolean;
  usersCount: number;
  createdAt: string;
  createdById: number;
  modifiedAt: string;
  modifiedById: number;
  status?: TeamStatus;
  isDeletionInProgress?: boolean;
  isDataMovementInProgress?: boolean;
  profilesCount?: number;
}

export interface GetTeamsResponse {
  count: number;
  teams: Team[];
  apiFailed: boolean;
}

export interface TeamsApiResponse {
  items: Team[];
  count?: number;
  totalSize?: number;
}

export interface UpdatePreferredTeamPayload {
  teamId: string;
}

export interface UpdatePreferredTeamResponse {
  teamId: string;
  teamName: string;
  createdAt: string;
  createdById: number;
  modifiedAt: string;
  modifiedById: number;
}

export interface TeamPreferenceResponse {
  teamId: string;
  teamName: string;
}

export interface ActiveUser {
  userId: number;
  firstName: string;
  lastName: string;
  fullName: string;
  email: string;
  status: string;
}

export interface GetActiveUsersResponse {
  items: ActiveUser[];
  count: number;
}

export interface AddTeamUserRequest {
  userId: number;
  roleId: number;
}

export type AddTeamMembersPayload = AddTeamUserRequest[];

export interface AddTeamMembersFailedItem {
  failedItem: AddTeamUserRequest;
  error?: ProblemDetails;
}

export interface AddTeamMembersResponse {
  successful: AddTeamUserRequest[];
  failed: AddTeamMembersFailedItem[];
}

export interface TeamMember {
  userId: number;
  firstName: string;
  lastName: string;
  fullName: string;
  email: string;
  roleId: number;
  roleName: string;
}

export interface TeamMemberApiResponse {
  userId: number;
  userName: string;
  roleId: number;
  roleName: string;
  createdById?: number;
  createdAt?: string;
  createdBy?: string | null;
  modifiedById?: number | null;
  modifiedAt?: string;
  modifiedBy?: string | null;
}

export interface GetTeamMembersResponse {
  items: TeamMember[];
  count: number;
}

export interface RemoveTeamMemberAssignment {
  userId: number;
  assignTo: number;
}

export type RemoveTeamMembersPayload = RemoveTeamMemberAssignment[];

export interface RemoveTeamMembersFailedItem {
  failedItem: RemoveTeamMemberAssignment;
  error?: ProblemDetails;
}

export interface RemoveTeamMembersResponse {
  successful: RemoveTeamMemberAssignment[];
  failed: RemoveTeamMembersFailedItem[];
}

export type ActionOnRecords = "Move" | "Delete";

export interface DeleteTeamPayload {
  teamId: string;
  permissionTeamId?: string;
  actionOnRecords: ActionOnRecords;
}

export interface TeamDetailsResponse {
  id: string;
  name: string;
  description?: string;
  isDefault?: boolean;
}

export type ActiveTeamAccessResult =
  | { kind: "ok"; teamName: string }
  | { kind: "teamDeleted" }
  | { kind: "userDeassigned" };
